import React, { useState, useCallback, useEffect, useRef } from 'react'
import './style.scss'

const SCROLL_BOX_MIN_HEIGHT = 20

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function CustomScrollDiv({ children, length = 0 }) {
  const [scrollBoxHeight, setScrollBoxHeight] = useState(SCROLL_BOX_MIN_HEIGHT)
  const [scrollBoxTop, setScrollBoxTop] = useState(0)
  const [lastScrollThumbPosition, setScrollThumbPosition] = useState(0)
  const scrollHostRef = useRef()
  const [isDragging, setDragging] = useState(false)

  const handleDocumentMouseUp = useCallback(
    e => {
      if (isDragging) {
        e.preventDefault()
        setDragging(false)
      }
    },
    [isDragging]
  )

  const handleDocumentMouseMove = useCallback(
    e => {
      if (isDragging) {
        e.preventDefault()
        e.stopPropagation()
        const scrollHostElement = scrollHostRef.current
        const { scrollHeight, offsetHeight } = scrollHostElement

        const deltaY = e.clientY - lastScrollThumbPosition
        const percentage = deltaY * (scrollHeight / offsetHeight)

        setScrollThumbPosition(e.clientY)
        setScrollBoxTop(
          Math.min(
            Math.max(0, scrollBoxTop + deltaY),
            offsetHeight - scrollBoxHeight
          )
        )
        scrollHostElement.scrollTop = Math.min(
          scrollHostElement.scrollTop + percentage,
          scrollHeight - offsetHeight
        )
      }
    },
    [isDragging, lastScrollThumbPosition, scrollBoxHeight, scrollBoxTop]
  )

  const handleScrollThumbMouseDown = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    setScrollThumbPosition(e.clientY)
    setDragging(true)
  }, [])

  const handleScroll = useCallback(() => {
    if (!scrollHostRef) {
      return
    }
    const scrollHostElement = scrollHostRef.current
    const { scrollTop, scrollHeight, offsetHeight } = scrollHostElement

    let newTop =
      (parseInt(scrollTop, 10) / parseInt(scrollHeight, 10)) * offsetHeight
    newTop = Math.min(newTop, offsetHeight - scrollBoxHeight)
    setScrollBoxTop(newTop)
  }, [])

  useEffect(() => {
    const scrollHostElement = scrollHostRef.current
    const { clientHeight, scrollHeight } = scrollHostElement
    const scrollThumbPercentage = clientHeight / scrollHeight
    const scrollThumbHeight = Math.max(
      scrollThumbPercentage * clientHeight,
      SCROLL_BOX_MIN_HEIGHT
    )

    setScrollBoxHeight(scrollThumbHeight)
    scrollHostElement.addEventListener('scroll', handleScroll, true)
    return function cleanup() {
      scrollHostElement.removeEventListener('scroll', handleScroll, true)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mousemove', handleDocumentMouseMove)
    document.addEventListener('mouseup', handleDocumentMouseUp)
    document.addEventListener('mouseleave', handleDocumentMouseUp)
    return function cleanup() {
      document.removeEventListener('mousemove', handleDocumentMouseMove)
      document.removeEventListener('mouseup', handleDocumentMouseUp)
      document.removeEventListener('mouseleave', handleDocumentMouseUp)
    }
  }, [handleDocumentMouseMove, handleDocumentMouseUp])

  return (
    <div
      className={
        length < 4 ? 'scrollhost-container-small' : 'scrollhost-container-big'
      }
    >
      <div ref={scrollHostRef} className="scrollhost">
        {children}
      </div>
      {length > 4 && (
        <div className="scroll-bar">
          <div
            role="presentation"
            className="scroll-thumb"
            style={{ height: scrollBoxHeight || '20px', top: scrollBoxTop }}
            onMouseDown={handleScrollThumbMouseDown}
          />
        </div>
      )}
    </div>
  )
}
